import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash, PencilSimple, User, Upload } from '@phosphor-icons/react';
import Modal from '../components/Modal';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CREW_ROLES = [
  'Director', 'DOP', 'Music Director', 'Editor', 'Writer', 'VFX Supervisor',
  'Production House', 'Lead Hero', 'Lead Heroine', 'Supporting Cast',
  'Choreographer', 'Sound Designer', 'Costume Designer', 'Producer', 'Other',
];

const emptyForm = {
  role: 'Director',
  custom_role: '',
  name: '',
  bio: '',
  known_for: '',
  photo_url: '',
  profile_url: '',
  display_order: 0,
};

const MovieCrewManage = ({ user }) => {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId, user]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/movies/${movieId}`),
        axios.get(`${BACKEND_URL}/api/movies/${movieId}/crew`),
      ]);
      setMovie(m.data);
      setCrew(c.data);
    } catch (err) {
      toast.error('Failed to load movie crew');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, display_order: crew.length });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      role: c.role || 'Director',
      custom_role: c.custom_role || '',
      name: c.name || '',
      bio: c.bio || '',
      known_for: c.known_for || '',
      photo_url: c.photo_url || '',
      profile_url: c.profile_url || '',
      display_order: c.display_order || 0,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name required');
    const payload = { ...form };
    payload.display_order = Number(payload.display_order) || 0;
    ['custom_role', 'bio', 'known_for', 'photo_url', 'profile_url'].forEach(k => {
      if (!payload[k]) delete payload[k];
    });
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/movies/${movieId}/crew/${editingId}`, payload, { headers: headers() });
        toast.success('Updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/movies/${movieId}/crew`, payload, { headers: headers() });
        toast.success('Added');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/movies/${movieId}/crew/${id}`, { headers: headers() });
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setRemoveTarget(null);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/uploads/producer-asset?kind=image`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, photo_url: res.data.url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-slate-400 font-body">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate(`/movie/${movieId}`)} className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="back-to-movie">
            <ArrowLeft size={18} /> <span className="font-body text-sm">Back to movie</span>
          </button>
          <button onClick={openCreate} data-testid="add-crew-btn" className="btn-primary text-sm py-2 flex items-center gap-1">
            <Plus size={14} weight="bold" /> Add crew
          </button>
        </div>
      </nav>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <p className="font-body text-sm text-slate-400 uppercase tracking-wide mb-1">Managing crew for</p>
          <h1 className="font-heading text-4xl font-bold">{movie?.title}</h1>
          <p className="font-body text-slate-400 mt-2">Add director, cast, technicians, production house — anyone investors should know about.</p>
        </div>

        {crew.length === 0 ? (
          <div className="bg-card border border-white/10 rounded-lg p-10 text-center">
            <User size={48} className="text-slate-400 mx-auto mb-3" />
            <p className="font-body text-slate-400 mb-4">No crew members yet. Add your director, leads, DOP, and production house to help investors evaluate this project.</p>
            <button onClick={openCreate} className="btn-primary text-sm py-2 inline-flex items-center gap-1">
              <Plus size={14} /> Add first crew member
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="crew-list">
            {crew.map(c => (
              <div key={c.id} className="bg-card border border-white/10 rounded-lg p-4 flex items-center gap-4" data-testid={`crew-row-${c.id}`}>
                {c.photo_url ? (
                  <img src={`${c.photo_url.startsWith('http') ? '' : BACKEND_URL}${c.photo_url}`} alt={c.name} className="w-16 h-16 rounded-full object-cover ring-1 ring-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/5 ring-1 ring-white/10 flex items-center justify-center">
                    <User size={28} className="text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-bold">{c.name}</h3>
                    <span className="text-[10px] uppercase tracking-wide text-gold border border-gold/30 bg-gold/10 px-2 py-0.5 rounded font-bold">
                      {c.role === 'Other' ? (c.custom_role || 'Other') : c.role}
                    </span>
                  </div>
                  {c.known_for && <p className="font-body text-sm text-slate-300 mt-1">Known for: {c.known_for}</p>}
                  {c.bio && <p className="font-body text-xs text-slate-400 mt-1 line-clamp-2">{c.bio}</p>}
                </div>
                <div className="flex gap-2">
                  <button aria-label="Edit crew member" data-testid={`edit-crew-${c.id}`} onClick={() => openEdit(c)} className="btn-secondary py-2 text-sm">
                    <PencilSimple size={14} />
                  </button>
                  <button aria-label="Delete crew member" data-testid={`delete-crew-${c.id}`} onClick={() => setRemoveTarget(c.id)} className="py-2 px-3 text-sm rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={editingId ? 'Edit crew member' : 'Add crew member'}>
          <form onSubmit={submit} data-testid="crew-form" className="bg-card border border-white/10 rounded-lg max-w-xl w-full max-h-[92vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold">{editingId ? 'Edit crew member' : 'Add crew member'}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="font-body text-xs text-slate-400">Role *</label>
                <select data-testid="crew-form-role" className="admin-input mt-1" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  {CREW_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              {form.role === 'Other' && (
                <div>
                  <label className="font-body text-xs text-slate-400">Custom role label</label>
                  <input data-testid="crew-form-custom-role" className="admin-input mt-1" value={form.custom_role} onChange={e => setForm({ ...form, custom_role: e.target.value })} placeholder="e.g., Action Director" />
                </div>
              )}
              <div className={form.role === 'Other' ? '' : 'md:col-span-2'}>
                <label className="font-body text-xs text-slate-400">Name *</label>
                <input data-testid="crew-form-name" required className="admin-input mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Known for (past notable works)</label>
                <input data-testid="crew-form-known-for" className="admin-input mt-1" value={form.known_for} onChange={e => setForm({ ...form, known_for: e.target.value })} placeholder="e.g., Echoes of Silence (2020), RRR (2022)" />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Short bio</label>
                <textarea data-testid="crew-form-bio" rows={2} className="admin-input mt-1" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Profile / IMDb link</label>
                <input data-testid="crew-form-profile-url" className="admin-input mt-1" value={form.profile_url} onChange={e => setForm({ ...form, profile_url: e.target.value })} placeholder="https://..." />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Display order</label>
                <input data-testid="crew-form-order" type="number" min="0" className="admin-input mt-1" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Photo</label>
                <div className="flex items-center gap-2 mt-1">
                  <input data-testid="crew-form-photo-upload" type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} className="text-xs flex-1" />
                  {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
                </div>
                {form.photo_url && <p className="text-xs text-emerald-400 mt-1" data-testid="crew-photo-uploaded">Photo attached</p>}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm py-2">Cancel</button>
              <button data-testid="crew-form-submit" type="submit" className="btn-primary text-sm py-2">{editingId ? 'Save' : 'Add'}</button>
            </div>
          </form>
        </Modal>
      )}

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this crew member?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(removeTarget)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MovieCrewManage;
